import QRCode from "qrcode";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import type { QrPlacement } from "./qrCodeTemplates";
import config from "../../../../../config.json";

GlobalWorkerOptions.workerSrc = pdfWorker;

export async function buildQrPreviewDataUrl(
  url: string,
  tableNumber: number | null,
  tableColor: string
): Promise<string> {
  const base = await QRCode.toDataURL(url, {
    errorCorrectionLevel: "H",
    margin: 1,
    width: 320,
    color: { dark: "#111111", light: "#ffffff" },
  });

  if (!tableNumber) return base;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(base);
        return;
      }

      ctx.drawImage(img, 0, 0);
      const text = String(tableNumber);
      const fontSize = Math.round(img.width * 0.14);
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      const textHeight = fontSize;
      const centerX = img.width / 2;
      const centerY = img.height / 2;
      const radius = Math.max(textWidth, textHeight) / 1.4 + 12;

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = /^#[0-9A-F]{6}$/i.test(tableColor) ? tableColor : "#fa8072";
      ctx.fill();

      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, centerX, centerY);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("QR preview failed"));
    img.src = base;
  });
}

export async function renderPdfFirstPageDataUrl(
  file: File,
  maxWidth = 1400
): Promise<{ dataUrl: string; width: number; height: number }> {
  const data = await file.arrayBuffer();
  const pdf = await getDocument({ data }).promise;
  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(2, maxWidth / baseViewport.width);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  await page.render({ canvasContext: ctx, viewport, canvas }).promise;

  return {
    dataUrl: canvas.toDataURL("image/png"),
    width: baseViewport.width,
    height: baseViewport.height,
  };
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

export function isAcceptedTemplateFile(file: File): boolean {
  const allowed = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
  ];
  return allowed.includes(file.type);
}

export function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Image dimensions failed"));
    img.src = dataUrl;
  });
}

export async function fetchTemplateAspectRatio(file: File, apiBase: string, token: string | null) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${apiBase}/v2/restaurant/qr-template-dimensions`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!response.ok) throw new Error("Template dimensions failed");
  const data = await response.json();
  return Number(data.aspectRatio) || 210 / 297;
}

export function placementToFormFields(placement: QrPlacement) {
  return {
    qrLeftPercent: String(placement.leftPercent),
    qrTopPercent: String(placement.topPercent),
    qrSizePercent: String(placement.sizePercent),
  };
}

/** Resolve download URL from generateQRcode API (UPLOADS_SERVER or legacy local path). */
export function resolveQrPdfDownloadUrl(data: {
  pdfUrl?: string | null;
  pdfLocalUrl?: string | null;
  pdfFilename?: string | null;
  apiBase?: string;
}): string {
  const candidates = listQrPdfDownloadCandidates(data);
  return candidates[0] || "";
}

/** Build ordered list of likely PDF URLs (CDN /uploads, CDN without uploads, local API). */
export function listQrPdfDownloadCandidates(data: {
  pdfUrl?: string | null;
  pdfLocalUrl?: string | null;
  pdfFilename?: string | null;
  apiBase?: string;
}): string[] {
  const apiBase = (data.apiBase || config.dev || "").replace(/\/+$/, "");
  const uploadsBase = String(config.UPLOADS_SERVER || "").replace(/\/+$/, "");
  const uploadsBaseAlt = uploadsBase.replace(/\/uploads$/i, "");
  const filename = data.pdfFilename?.trim() || "";
  const encoded = filename
    ? filename
        .split("/")
        .filter(Boolean)
        .map(encodeURIComponent)
        .join("/")
    : "";
  const preferLocal = /localhost|127\.0\.0\.1/i.test(apiBase);

  const out: string[] = [];
  const push = (url?: string | null) => {
    const value = String(url || "").trim();
    if (!value) return;
    const absolute = /^https?:\/\//i.test(value)
      ? value
      : `${apiBase}/${value.replace(/^\/+/, "")}`;
    if (!out.includes(absolute)) out.push(absolute);
  };

  const pushLocal = () => {
    push(data.pdfLocalUrl);
    if (encoded && apiBase) push(`${apiBase}/uploads/qrcodes/${encoded}`);
  };

  const pushRemote = () => {
    push(data.pdfUrl);
    if (encoded) {
      if (uploadsBase) push(`${uploadsBase}/qrcodes/${encoded}`);
      if (uploadsBaseAlt && uploadsBaseAlt !== uploadsBase) {
        push(`${uploadsBaseAlt}/uploads/qrcodes/${encoded}`);
        push(`${uploadsBaseAlt}/qrcodes/${encoded}`);
      }
    }
  };

  // Local API first in dev — remote CDN often 404s when upload falls back to disk
  if (preferLocal) {
    pushLocal();
    pushRemote();
  } else {
    pushRemote();
    pushLocal();
  }

  return out;
}

export async function triggerPdfDownload(
  source:
    | string
    | {
        pdfUrl?: string | null;
        pdfLocalUrl?: string | null;
        pdfFilename?: string | null;
      },
  fileName = "qrcode.pdf"
) {
  const meta =
    typeof source === "string"
      ? { pdfUrl: source }
      : {
          pdfUrl: source.pdfUrl,
          pdfLocalUrl: source.pdfLocalUrl,
          pdfFilename: source.pdfFilename,
        };

  const candidates = listQrPdfDownloadCandidates({
    ...meta,
    apiBase: config.dev,
  });

  if (!candidates.length) {
    throw new Error("No PDF download URL available");
  }

  const token = localStorage.getItem("tokenLogin");
  let lastError: Error | null = null;

  for (const candidate of candidates) {
    try {
      const response = await fetch(candidate, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        lastError = new Error(`PDF download failed (${response.status})`);
        continue;
      }
      const blob = await response.blob();
      if (!blob || blob.size < 50) {
        lastError = new Error("PDF download returned an empty file");
        continue;
      }
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      return;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
    }
  }

  throw lastError || new Error("PDF download failed");
}
